import { useCallback, useEffect, useRef } from 'react'
import { classifyError } from './classifyError'
import { getErrorMessage, getErrorSignature } from './errorHelpers'
import { trackMultiply } from './events'
import type { MarketType } from './types'

interface UseTrackedMultiplyActionParams {
  marketType: MarketType
  collateralToken: string
  collateralMarket: string
  collateralAmount: string
  liabilityToken: string
  liabilityMarket: string
  borrowAmount: string
  multiplier?: number
  txHash?: string
  isConfirmed: boolean
  error?: unknown
}

export function useTrackedMultiplyAction({
  marketType,
  collateralToken,
  collateralMarket,
  collateralAmount,
  liabilityToken,
  liabilityMarket,
  borrowAmount,
  multiplier,
  txHash,
  isConfirmed,
  error,
}: UseTrackedMultiplyActionParams): { markSubmitted: () => void } {
  const activeAttemptRef = useRef(0)
  const finalizedAttemptRef = useRef(0)
  const submitTimeRef = useRef(0)
  const lastSignedTxHashRef = useRef<string | undefined>(undefined)
  const lastErrorSignatureRef = useRef('')

  const markSubmitted = useCallback(() => {
    activeAttemptRef.current += 1
    finalizedAttemptRef.current = 0
    submitTimeRef.current = Date.now()
    lastSignedTxHashRef.current = undefined
    lastErrorSignatureRef.current = getErrorSignature(error)

    trackMultiply('submitted', {
      market_type: marketType,
      collateral_token: collateralToken,
      collateral_market: collateralMarket,
      collateral_amount: collateralAmount,
      liability_token: liabilityToken,
      liability_market: liabilityMarket,
      borrow_amount: borrowAmount,
      multiplier,
    })
  }, [
    borrowAmount,
    collateralAmount,
    collateralMarket,
    collateralToken,
    error,
    liabilityMarket,
    liabilityToken,
    marketType,
    multiplier,
  ])

  useEffect(() => {
    if (!txHash || activeAttemptRef.current === 0 || lastSignedTxHashRef.current === txHash) {
      return
    }

    lastSignedTxHashRef.current = txHash

    trackMultiply('signed', {
      market_type: marketType,
      collateral_token: collateralToken,
      collateral_market: collateralMarket,
      collateral_amount: collateralAmount,
      liability_token: liabilityToken,
      liability_market: liabilityMarket,
      borrow_amount: borrowAmount,
      multiplier,
      tx_hash: txHash,
    })
  }, [
    borrowAmount,
    collateralAmount,
    collateralMarket,
    collateralToken,
    liabilityMarket,
    liabilityToken,
    marketType,
    multiplier,
    txHash,
  ])

  useEffect(() => {
    if (
      !isConfirmed ||
      !txHash ||
      activeAttemptRef.current === 0 ||
      finalizedAttemptRef.current === activeAttemptRef.current
    ) {
      return
    }

    finalizedAttemptRef.current = activeAttemptRef.current

    trackMultiply('completed', {
      market_type: marketType,
      collateral_token: collateralToken,
      collateral_market: collateralMarket,
      collateral_amount: collateralAmount,
      liability_token: liabilityToken,
      liability_market: liabilityMarket,
      borrow_amount: borrowAmount,
      multiplier,
      tx_hash: txHash,
      duration_ms: submitTimeRef.current > 0 ? Date.now() - submitTimeRef.current : 0,
    })
  }, [
    borrowAmount,
    collateralAmount,
    collateralMarket,
    collateralToken,
    isConfirmed,
    liabilityMarket,
    liabilityToken,
    marketType,
    multiplier,
    txHash,
  ])

  useEffect(() => {
    const errorSignature = getErrorSignature(error)
    if (
      !error ||
      !errorSignature ||
      activeAttemptRef.current === 0 ||
      finalizedAttemptRef.current === activeAttemptRef.current ||
      errorSignature === lastErrorSignatureRef.current
    ) {
      return
    }

    lastErrorSignatureRef.current = errorSignature
    finalizedAttemptRef.current = activeAttemptRef.current

    trackMultiply('failed', {
      market_type: marketType,
      collateral_token: collateralToken,
      collateral_market: collateralMarket,
      collateral_amount: collateralAmount,
      liability_token: liabilityToken,
      liability_market: liabilityMarket,
      borrow_amount: borrowAmount,
      multiplier,
      error_type: classifyError(error),
      error_message: getErrorMessage(error),
      failed_at: txHash || lastSignedTxHashRef.current ? 'confirmation' : 'sign',
    })
  }, [
    borrowAmount,
    collateralAmount,
    collateralMarket,
    collateralToken,
    error,
    liabilityMarket,
    liabilityToken,
    marketType,
    multiplier,
    txHash,
  ])

  return { markSubmitted }
}
